import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  AlertTriangle, Upload, Trash2, Check, X, RefreshCw,
  FileSpreadsheet, Users, Briefcase, Download, ArrowRight,
  ArrowLeft, Database, RotateCcw, Eye, CheckCircle2, XCircle,
  AlertCircle, Info, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BPStatusDiagnosticsPanel } from '../components/BPStatusDiagnosticsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Import Wizard Steps
const WIZARD_STEPS = [
  { id: 'upload', label: 'Upload Files', icon: Upload },
  { id: 'preview', label: 'Preview & Map', icon: Eye },
  { id: 'validate', label: 'Validate', icon: CheckCircle2 },
  { id: 'options', label: 'Options', icon: Database },
  { id: 'import', label: 'Import', icon: ArrowRight },
  { id: 'report', label: 'Report', icon: FileSpreadsheet }
];

// Builder Prime Sync Panel Component
const BuilderPrimeSyncPanel = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deltaSync, setDeltaSync] = useState(false);

  useEffect(() => {
    fetchSyncStatus();
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/integrations/builderprime/status`);
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error fetching sync status:', error);
      toast.error('Failed to fetch Builder Prime status');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/integrations/builderprime/test-connection`);
      if (response.data.success) {
        toast.success('Builder Prime connection successful!');
      } else {
        toast.error(response.data.error || 'Connection test failed');
      }
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const fullBackfill = async () => {
    if (!window.confirm('This will import ALL leads from Builder Prime. Continue?')) return;
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/integrations/builderprime/sync/full`);
      if (response.data.success) {
        const stats = response.data.stats;
        toast.success(`Imported ${stats.fetched} leads! (${stats.created} new, ${stats.updated} updated)`);
        fetchSyncStatus();
      } else {
        toast.error(response.data.error || 'Backfill failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backfill failed');
    } finally {
      setSyncing(false);
    }
  };

  const runDeltaSync = async () => {
    setDeltaSync(true);
    try {
      const response = await axios.post(`${API}/integrations/builderprime/sync/delta?hours_back=24`);
      if (response.data.success) {
        const stats = response.data.stats;
        toast.success(`Delta sync complete! (${stats.created} new, ${stats.updated} updated)`);
        fetchSyncStatus();
      } else {
        toast.error(response.data.error || 'Delta sync failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delta sync failed');
    } finally {
      setDeltaSync(false);
    }
  };

  const [syncingProjects, setSyncingProjects] = useState(false);
  const [syncingEmployees, setSyncingEmployees] = useState(false);

  const syncProjects = async () => {
    if (!window.confirm('This will import ALL projects from Builder Prime. Continue?')) return;
    
    setSyncingProjects(true);
    try {
      const response = await axios.post(`${API}/integrations/builderprime/sync/projects`);
      if (response.data.success) {
        const stats = response.data.stats;
        toast.success(`Imported ${stats.fetched} projects! (${stats.created} new, ${stats.updated} updated)`);
        fetchSyncStatus();
      } else {
        toast.error(response.data.error || 'Project sync failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Project sync failed');
    } finally {
      setSyncingProjects(false);
    }
  };

  const syncEmployees = async () => {
    setSyncingEmployees(true);
    try {
      const response = await axios.post(`${API}/integrations/builderprime/sync/employees`);
      if (response.data.success) {
        const stats = response.data.stats;
        toast.success(`Synced ${stats.fetched} employees! (${stats.matched} matched to users)`);
        fetchSyncStatus();
      } else {
        toast.error(response.data.error || 'Employee sync failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Employee sync failed');
    } finally {
      setSyncingEmployees(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
          <p className="mt-2 text-slate-400">Loading sync status...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Connection Status */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Builder Prime Integration Status
          </CardTitle>
          <CardDescription>
            API connection status and sync statistics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* API Status */}
          <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${syncStatus?.api_configured ? 'bg-green-500' : 'bg-red-500'}`} />
              <div>
                <p className="font-medium">API Configuration</p>
                <p className="text-xs text-slate-400">
                  {syncStatus?.api_configured 
                    ? `Connected to ${syncStatus?.subdomain || 'Builder Prime'}` 
                    : 'API key not configured'}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={testConnection}
              disabled={testing || !syncStatus?.api_configured}
            >
              {testing ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Test Connection'}
            </Button>
          </div>

          {/* Sync Stats */}
          {syncStatus?.stats && (
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-slate-800 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-400">
                  {syncStatus.stats.leads?.from_builderprime?.toLocaleString() || 0}
                </div>
                <p className="text-xs text-slate-400">BP Leads</p>
                <p className="text-xs text-slate-500 mt-1">
                  {syncStatus.stats.leads?.test_data || 0} test
                </p>
              </div>
              <div className="p-4 bg-slate-800 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-400">
                  {syncStatus.stats.jobs?.from_builderprime?.toLocaleString() || 0}
                </div>
                <p className="text-xs text-slate-400">BP Projects</p>
                <p className="text-xs text-slate-500 mt-1">
                  {syncStatus.stats.jobs?.test_data || 0} test
                </p>
              </div>
              <div className="p-4 bg-slate-800 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-400">
                  {syncStatus.stats.leads?.total?.toLocaleString() || 0}
                </div>
                <p className="text-xs text-slate-400">Total Leads</p>
              </div>
              <div className="p-4 bg-slate-800 rounded-lg text-center">
                <div className="text-2xl font-bold text-orange-400">
                  {syncStatus.stats.jobs?.total?.toLocaleString() || 0}
                </div>
                <p className="text-xs text-slate-400">Total Projects</p>
              </div>
            </div>
          )}

          {/* Sync Actions - Leads */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-300">Leads Sync</p>
            <div className="flex gap-3">
              <Button 
                onClick={fullBackfill}
                disabled={syncing || !syncStatus?.connection?.success}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {syncing ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Importing...</>
                ) : (
                  <><Database className="h-4 w-4 mr-2" /> Full Leads Backfill</>
                )}
              </Button>
              <Button 
                onClick={runDeltaSync}
                disabled={deltaSync || !syncStatus?.connection?.success}
                variant="outline"
                className="flex-1"
              >
                {deltaSync ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Syncing...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Sync Last 24h</>
                )}
              </Button>
            </div>
          </div>

          {/* Sync Actions - Projects & Employees */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-300">Projects & Employees</p>
            <div className="flex gap-3">
              <Button 
                onClick={syncProjects}
                disabled={syncingProjects || !syncStatus?.connection?.success}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {syncingProjects ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Importing...</>
                ) : (
                  <><Database className="h-4 w-4 mr-2" /> Sync Projects</>
                )}
              </Button>
              <Button 
                onClick={syncEmployees}
                disabled={syncingEmployees || !syncStatus?.connection?.success}
                variant="outline"
                className="flex-1"
              >
                {syncingEmployees ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Syncing...</>
                ) : (
                  <><Users className="h-4 w-4 mr-2" /> Sync Employees</>
                )}
              </Button>
            </div>
          </div>

          {/* Success Info Box */}
          {syncStatus?.connection?.success && (
            <div className="p-4 bg-green-950/30 border border-green-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-400 mt-0.5" />
                <div className="text-sm text-slate-300">
                  <p className="font-medium mb-1">Full API Access Enabled!</p>
                  <ul className="text-xs text-slate-400 space-y-1">
                    <li>• Leads: Full backfill + delta sync supported</li>
                    <li>• Projects: Full backfill supported</li>
                    <li>• Employees: Sync + user matching supported</li>
                    <li>• Bi-directional: Create/update leads syncs to BP</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Field Mappings */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm">Field Mappings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <p className="font-medium text-slate-300 mb-2">Lead Fields (Rhino → BP)</p>
              <div className="space-y-1 text-slate-400">
                <div className="flex justify-between"><span>first_name</span><span>→ firstName</span></div>
                <div className="flex justify-between"><span>last_name</span><span>→ lastName</span></div>
                <div className="flex justify-between"><span>email</span><span>→ email</span></div>
                <div className="flex justify-between"><span>phone</span><span>→ phone</span></div>
                <div className="flex justify-between"><span>address</span><span>→ address</span></div>
              </div>
            </div>
            <div>
              <p className="font-medium text-slate-300 mb-2">Project Fields (CSV Import)</p>
              <div className="space-y-1 text-slate-400">
                <div className="flex justify-between"><span>Job #</span><span>→ external_job_id</span></div>
                <div className="flex justify-between"><span>Client</span><span>→ customer_name</span></div>
                <div className="flex justify-between"><span>Project Type</span><span>→ job_type</span></div>
                <div className="flex justify-between"><span>Class</span><span>→ market_id</span></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Purge Test Data Panel Component
const PurgeTestDataPanel = () => {
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [confirmChecks, setConfirmChecks] = useState({ understand: false, backup: true });

  const identifyTestData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/integrations/builderprime/identify-test-data`);
      setTestData(response.data);
    } catch (error) {
      toast.error('Failed to identify test data');
    } finally {
      setLoading(false);
    }
  };

  const previewPurge = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/integrations/builderprime/purge-test-data`, {
        confirm: false,
        dry_run: true,
        backup: true
      });
      setTestData(prev => ({ ...prev, preview: response.data }));
    } catch (error) {
      toast.error('Failed to preview purge');
    } finally {
      setLoading(false);
    }
  };

  const executePurge = async () => {
    if (confirmText !== 'PURGE TEST DATA') {
      toast.error('Please type PURGE TEST DATA to confirm');
      return;
    }
    if (!confirmChecks.understand) {
      toast.error('Please confirm you understand this action');
      return;
    }

    setPurging(true);
    try {
      const response = await axios.post(`${API}/integrations/builderprime/purge-test-data`, {
        confirm: true,
        dry_run: false,
        backup: confirmChecks.backup
      });
      
      toast.success(`Purged ${response.data.total_deleted} test records. Backup ID: ${response.data.backup_id || 'N/A'}`);
      setTestData(null);
      setConfirmText('');
      setConfirmChecks({ understand: false, backup: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  return (
    <Card className="border-orange-500/50 bg-orange-950/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-orange-400">
          <AlertTriangle className="h-5 w-5" />
          Purge Test/Sample Data
        </CardTitle>
        <CardDescription>
          Remove test and sample data while preserving real Builder Prime records.
          Records with external_source=BuilderPrime will NOT be deleted.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Step 1: Identify Test Data */}
        <div>
          <Button 
            variant="outline" 
            onClick={identifyTestData}
            disabled={loading}
            className="w-full"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Scan for Test Data
          </Button>
        </div>

        {/* Test Data Results */}
        {testData && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-slate-800 rounded-lg text-center">
                <div className="text-2xl font-bold text-orange-400">
                  {testData.summary?.total_test_records || testData.total_test_records || 0}
                </div>
                <p className="text-xs text-slate-400">Test Records</p>
              </div>
              <div className="p-4 bg-slate-800 rounded-lg text-center">
                <div className="text-xl font-bold text-red-400">
                  {testData.summary?.breakdown?.leads || testData.leads?.count || 0}
                </div>
                <p className="text-xs text-slate-400">Test Leads</p>
              </div>
              <div className="p-4 bg-slate-800 rounded-lg text-center">
                <div className="text-xl font-bold text-red-400">
                  {testData.summary?.breakdown?.jobs || testData.jobs?.count || 0}
                </div>
                <p className="text-xs text-slate-400">Test Projects</p>
              </div>
              <div className="p-4 bg-slate-800 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-400">
                  {testData.total_real_records || 0}
                </div>
                <p className="text-xs text-slate-400">Real Records (Safe)</p>
              </div>
            </div>

            {/* Detection Reasons */}
            {testData.leads?.reasons && Object.keys(testData.leads.reasons).length > 0 && (
              <div className="p-4 bg-slate-800 rounded-lg">
                <p className="text-sm font-medium mb-2">Sample Detection Reasons:</p>
                <div className="text-xs text-slate-400 space-y-1 max-h-32 overflow-y-auto">
                  {Object.entries(testData.leads.reasons).slice(0, 5).map(([id, reasons]) => (
                    <div key={id} className="flex items-start gap-2">
                      <span className="text-orange-400">•</span>
                      <span>{reasons.join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmation Section */}
            <div className="space-y-4 pt-4 border-t border-slate-700">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="understand" 
                  checked={confirmChecks.understand}
                  onCheckedChange={(checked) => setConfirmChecks(prev => ({ ...prev, understand: checked }))}
                />
                <Label htmlFor="understand" className="text-sm">
                  I understand this will permanently delete test data
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="backup" 
                  checked={confirmChecks.backup}
                  onCheckedChange={(checked) => setConfirmChecks(prev => ({ ...prev, backup: checked }))}
                />
                <Label htmlFor="backup" className="text-sm">
                  Create backup before deletion (recommended)
                </Label>
              </div>

              <div>
                <Label htmlFor="confirmPurge" className="text-sm text-slate-400">
                  Type <span className="font-mono text-orange-400">PURGE TEST DATA</span> to confirm
                </Label>
                <Input
                  id="confirmPurge"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="PURGE TEST DATA"
                  className="mt-1 bg-slate-800 border-slate-700"
                />
              </div>

              <Button 
                variant="destructive"
                onClick={executePurge}
                disabled={purging || confirmText !== 'PURGE TEST DATA' || !confirmChecks.understand}
                className="w-full"
              >
                {purging ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Purge Test Data ({testData.summary?.total_test_records || testData.total_test_records || 0} records)
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const DataTools = () => {
  const [activeTab, setActiveTab] = useState('sync');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Reset state
  const [confirmText, setConfirmText] = useState('');
  const [confirmChecks, setConfirmChecks] = useState({ deleteCustomers: false, irreversible: false });
  const [resetting, setResetting] = useState(false);
  
  // Import wizard state
  const [wizardStep, setWizardStep] = useState(0);
  const [customersFile, setCustomersFile] = useState(null);
  const [projectsFile, setProjectsFile] = useState(null);
  const [customersUpload, setCustomersUpload] = useState(null);
  const [projectsUpload, setProjectsUpload] = useState(null);
  const [uploadingCustomers, setUploadingCustomers] = useState(false);
  const [uploadingProjects, setUploadingProjects] = useState(false);
  const [importOptions, setImportOptions] = useState({
    create_missing_users: true,
    mark_users_inactive: true,
    normalize_names: true,
    drop_empty_columns: true,
    create_missing_markets: true
  });
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  
  // Import batches
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchBatches();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/data-tools/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    setLoadingBatches(true);
    try {
      const response = await axios.get(`${API}/data-tools/batches`);
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleReset = async () => {
    if (confirmText !== 'RESET ALL DATA') {
      toast.error('Please type RESET ALL DATA to confirm');
      return;
    }
    if (!confirmChecks.deleteCustomers || !confirmChecks.irreversible) {
      toast.error('Please confirm both checkboxes');
      return;
    }

    setResetting(true);
    try {
      const response = await axios.post(`${API}/data-tools/reset`, {
        confirmation_text: confirmText,
        confirm_delete_customers: confirmChecks.deleteCustomers,
        confirm_irreversible: confirmChecks.irreversible
      });
      
      toast.success(`Data reset complete! Deleted ${response.data.deleted.customers} customers, ${response.data.deleted.jobs} jobs`);
      setConfirmText('');
      setConfirmChecks({ deleteCustomers: false, irreversible: false });
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleCustomersUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCustomersFile(file);
    setUploadingCustomers(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/data-tools/upload/customers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCustomersUpload(response.data);
      toast.success(`Customers file uploaded: ${response.data.total_rows} rows`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setCustomersFile(null);
    } finally {
      setUploadingCustomers(false);
    }
  };

  const handleProjectsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setProjectsFile(file);
    setUploadingProjects(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/data-tools/upload/projects`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProjectsUpload(response.data);
      toast.success(`Projects file uploaded: ${response.data.total_rows} rows`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setProjectsFile(null);
    } finally {
      setUploadingProjects(false);
    }
  };

  const handleRunImport = async () => {
    if (!projectsUpload) {
      toast.error('Projects file is required');
      return;
    }

    setImporting(true);
    setImportProgress(10);
    
    const formData = new FormData();
    formData.append('projects_upload_id', projectsUpload.upload_id);
    if (customersUpload) {
      formData.append('customers_upload_id', customersUpload.upload_id);
    }
    formData.append('options', JSON.stringify(importOptions));
    
    try {
      setImportProgress(30);
      const response = await axios.post(`${API}/data-tools/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportProgress(100);
      setImportResult(response.data);
      setWizardStep(5); // Go to report step
      toast.success('Import completed successfully!');
      fetchStats();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleRollback = async (batchId) => {
    if (!window.confirm('Are you sure you want to rollback this import? This will delete all records created by this batch.')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/data-tools/batches/${batchId}/rollback`);
      toast.success(`Rollback complete! Deleted ${response.data.deleted.jobs} jobs, ${response.data.deleted.customers} customers`);
      fetchStats();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rollback failed');
    }
  };

  const resetWizard = () => {
    setWizardStep(0);
    setCustomersFile(null);
    setProjectsFile(null);
    setCustomersUpload(null);
    setProjectsUpload(null);
    setImportResult(null);
    setImportProgress(0);
  };

  const canProceed = () => {
    switch (wizardStep) {
      case 0: return projectsUpload !== null;
      case 1: return true;
      case 2: return projectsUpload?.errors?.length === 0;
      case 3: return true;
      case 4: return !importing;
      default: return true;
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Data Tools</h1>
            <p className="text-muted-foreground">Import & Reset functionality for test data</p>
          </div>
          <Button variant="outline" onClick={() => { fetchStats(); fetchBatches(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Current Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{stats.customers}</div>
                <p className="text-xs text-slate-400">Customers</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{stats.jobs}</div>
                <p className="text-xs text-slate-400">Jobs</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{stats.users}</div>
                <p className="text-xs text-slate-400">Users</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{stats.markets}</div>
                <p className="text-xs text-slate-400">Markets</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-400">{stats.imported_jobs}</div>
                <p className="text-xs text-slate-400">Imported Jobs</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{stats.import_batches}</div>
                <p className="text-xs text-slate-400">Import Batches</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="sync">
              <RefreshCw className="h-4 w-4 mr-2" />
              BP Sync
            </TabsTrigger>
            <TabsTrigger value="parity" data-testid="status-parity-tab">
              <Shield className="h-4 w-4 mr-2" />
              Status Parity
            </TabsTrigger>
            <TabsTrigger value="reset">
              <Trash2 className="h-4 w-4 mr-2" />
              Purge Test Data
            </TabsTrigger>
            <TabsTrigger value="import">
              <Upload className="h-4 w-4 mr-2" />
              CSV Import
            </TabsTrigger>
            <TabsTrigger value="history">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Builder Prime Sync Tab */}
          <TabsContent value="sync" className="space-y-4">
            <BuilderPrimeSyncPanel />
          </TabsContent>

          {/* Status Parity Diagnostics Tab */}
          <TabsContent value="parity" className="space-y-4">
            <BPStatusDiagnosticsPanel />
          </TabsContent>

          {/* Reset Test Data Tab */}
          <TabsContent value="reset" className="space-y-4">
            <PurgeTestDataPanel />
          </TabsContent>

          {/* Import Tab */}
          <TabsContent value="import" className="space-y-4">
            {/* Wizard Steps */}
            <div className="flex items-center justify-between mb-6 px-4">
              {WIZARD_STEPS.map((step, idx) => {
                const Icon = step.icon;
                const isActive = idx === wizardStep;
                const isComplete = idx < wizardStep;
                
                return (
                  <React.Fragment key={step.id}>
                    <div className="flex flex-col items-center gap-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        isActive ? 'bg-blue-600 text-white' :
                        isComplete ? 'bg-green-600 text-white' :
                        'bg-slate-800 text-slate-400'
                      }`}>
                        {isComplete ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                      </div>
                      <span className={`text-xs ${isActive ? 'text-white' : 'text-slate-500'}`}>
                        {step.label}
                      </span>
                    </div>
                    {idx < WIZARD_STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 ${idx < wizardStep ? 'bg-green-600' : 'bg-slate-700'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Step Content */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                {/* Step 0: Upload */}
                {wizardStep === 0 && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Upload Builder Prime Export Files</h3>
                      <p className="text-sm text-slate-400 mb-6">
                        Upload your Builder Prime CSV exports. The Projects file is required.
                        Customers file is optional but recommended for better data linking.
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Customers File */}
                      <div className="border border-dashed border-slate-700 rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Users className="h-8 w-8 text-blue-400" />
                          <div>
                            <h4 className="font-medium">Customers File</h4>
                            <p className="text-xs text-slate-400">Optional - .csv or .xlsx</p>
                          </div>
                        </div>
                        
                        {customersUpload ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-green-400">
                              <CheckCircle2 className="h-4 w-4" />
                              <span className="text-sm">{customersFile?.name}</span>
                            </div>
                            <p className="text-xs text-slate-400">{customersUpload.total_rows} rows detected</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setCustomersFile(null); setCustomersUpload(null); }}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <input
                              type="file"
                              accept=".csv,.xlsx"
                              onChange={handleCustomersUpload}
                              className="hidden"
                              id="customers-file"
                              disabled={uploadingCustomers}
                            />
                            <label htmlFor="customers-file">
                              <Button variant="outline" className="w-full" asChild disabled={uploadingCustomers}>
                                <span>
                                  {uploadingCustomers ? (
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="h-4 w-4 mr-2" />
                                  )}
                                  {uploadingCustomers ? 'Uploading...' : 'Select Customers File'}
                                </span>
                              </Button>
                            </label>
                          </div>
                        )}
                      </div>

                      {/* Projects File */}
                      <div className="border border-dashed border-slate-700 rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Briefcase className="h-8 w-8 text-green-400" />
                          <div>
                            <h4 className="font-medium">Projects File <Badge variant="secondary" className="ml-2">Required</Badge></h4>
                            <p className="text-xs text-slate-400">.csv or .xlsx</p>
                          </div>
                        </div>
                        
                        {projectsUpload ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-green-400">
                              <CheckCircle2 className="h-4 w-4" />
                              <span className="text-sm">{projectsFile?.name}</span>
                            </div>
                            <p className="text-xs text-slate-400">{projectsUpload.total_rows} rows detected</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setProjectsFile(null); setProjectsUpload(null); }}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <input
                              type="file"
                              accept=".csv,.xlsx"
                              onChange={handleProjectsUpload}
                              className="hidden"
                              id="projects-file"
                              disabled={uploadingProjects}
                            />
                            <label htmlFor="projects-file">
                              <Button variant="outline" className="w-full" asChild disabled={uploadingProjects}>
                                <span>
                                  {uploadingProjects ? (
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="h-4 w-4 mr-2" />
                                  )}
                                  {uploadingProjects ? 'Uploading...' : 'Select Projects File'}
                                </span>
                              </Button>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 1: Preview */}
                {wizardStep === 1 && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold">Preview & Column Mapping</h3>
                    
                    {/* Projects Preview */}
                    {projectsUpload && (
                      <div>
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <Briefcase className="h-4 w-4" />
                          Projects Preview ({projectsUpload.total_rows} rows)
                        </h4>
                        <div className="border border-slate-700 rounded-lg overflow-x-auto max-h-64">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-800 sticky top-0">
                              <tr>
                                {projectsUpload.columns.slice(0, 8).map((col, idx) => (
                                  <th key={idx} className="px-3 py-2 text-left text-xs font-medium">
                                    {col}
                                    {projectsUpload.auto_mappings[col] && (
                                      <Badge variant="outline" className="ml-1 text-xs">
                                        → {projectsUpload.auto_mappings[col]}
                                      </Badge>
                                    )}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {projectsUpload.preview.slice(0, 10).map((row, idx) => (
                                <tr key={idx} className="border-t border-slate-800">
                                  {projectsUpload.columns.slice(0, 8).map((col, colIdx) => (
                                    <td key={colIdx} className="px-3 py-2 text-xs truncate max-w-[150px]">
                                      {row[col] || '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Customers Preview */}
                    {customersUpload && (
                      <div>
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Customers Preview ({customersUpload.total_rows} rows)
                        </h4>
                        <div className="border border-slate-700 rounded-lg overflow-x-auto max-h-64">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-800 sticky top-0">
                              <tr>
                                {customersUpload.columns.slice(0, 6).map((col, idx) => (
                                  <th key={idx} className="px-3 py-2 text-left text-xs font-medium">{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {customersUpload.preview.slice(0, 5).map((row, idx) => (
                                <tr key={idx} className="border-t border-slate-800">
                                  {customersUpload.columns.slice(0, 6).map((col, colIdx) => (
                                    <td key={colIdx} className="px-3 py-2 text-xs truncate max-w-[150px]">
                                      {row[col] || '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Validate */}
                {wizardStep === 2 && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold">Validation Results</h3>
                    
                    {/* Projects Validation */}
                    {projectsUpload && (
                      <Card className="bg-slate-800 border-slate-700">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Briefcase className="h-4 w-4" />
                            Projects Validation
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {projectsUpload.errors?.length > 0 ? (
                            <div className="space-y-2">
                              {projectsUpload.errors.map((err, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-red-400 text-sm">
                                  <XCircle className="h-4 w-4" />
                                  <span>{err.message}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-green-400">
                              <CheckCircle2 className="h-4 w-4" />
                              <span>No critical errors found</span>
                            </div>
                          )}
                          
                          {projectsUpload.warnings?.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <p className="text-sm text-yellow-400 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                Warnings ({projectsUpload.warnings.length})
                              </p>
                              {projectsUpload.warnings.slice(0, 5).map((warn, idx) => (
                                <div key={idx} className="text-xs text-slate-400 ml-6">
                                  {warn.message}
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Step 3: Options */}
                {wizardStep === 3 && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold">Import Options</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div>
                          <p className="font-medium">Create missing users</p>
                          <p className="text-xs text-slate-400">Auto-create users for Sales Person / Project Manager</p>
                        </div>
                        <Checkbox
                          checked={importOptions.create_missing_users}
                          onCheckedChange={(checked) => setImportOptions(prev => ({ ...prev, create_missing_users: checked }))}
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div>
                          <p className="font-medium">Mark users as inactive</p>
                          <p className="text-xs text-slate-400">Created users will be marked as pending invite</p>
                        </div>
                        <Checkbox
                          checked={importOptions.mark_users_inactive}
                          onCheckedChange={(checked) => setImportOptions(prev => ({ ...prev, mark_users_inactive: checked }))}
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div>
                          <p className="font-medium">Normalize names</p>
                          <p className="text-xs text-slate-400">Trim whitespace and standardize formatting</p>
                        </div>
                        <Checkbox
                          checked={importOptions.normalize_names}
                          onCheckedChange={(checked) => setImportOptions(prev => ({ ...prev, normalize_names: checked }))}
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div>
                          <p className="font-medium">Create missing markets</p>
                          <p className="text-xs text-slate-400">Auto-create markets from Class column</p>
                        </div>
                        <Checkbox
                          checked={importOptions.create_missing_markets}
                          onCheckedChange={(checked) => setImportOptions(prev => ({ ...prev, create_missing_markets: checked }))}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Import */}
                {wizardStep === 4 && (
                  <div className="space-y-6 text-center py-8">
                    <h3 className="text-lg font-semibold">Ready to Import</h3>
                    
                    <div className="max-w-md mx-auto space-y-4">
                      <div className="p-4 bg-slate-800 rounded-lg">
                        <p className="text-sm text-slate-400 mb-2">Projects to import:</p>
                        <p className="text-2xl font-bold">{projectsUpload?.total_rows || 0}</p>
                      </div>
                      
                      {customersUpload && (
                        <div className="p-4 bg-slate-800 rounded-lg">
                          <p className="text-sm text-slate-400 mb-2">Customers to import:</p>
                          <p className="text-2xl font-bold">{customersUpload.total_rows}</p>
                        </div>
                      )}

                      {importing && (
                        <div className="space-y-2">
                          <Progress value={importProgress} className="h-2" />
                          <p className="text-sm text-slate-400">Importing... {importProgress}%</p>
                        </div>
                      )}

                      <Button
                        onClick={handleRunImport}
                        disabled={importing}
                        className="w-full bg-green-600 hover:bg-green-700"
                        size="lg"
                      >
                        {importing ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <ArrowRight className="h-4 w-4 mr-2" />
                            Start Import
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 5: Report */}
                {wizardStep === 5 && importResult && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <CheckCircle2 className="h-16 w-16 text-green-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold">Import Complete!</h3>
                      <p className="text-slate-400">Batch ID: {importResult.batch_id}</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="bg-green-950/30 border-green-800/50">
                        <CardContent className="pt-4 text-center">
                          <p className="text-2xl font-bold text-green-400">{importResult.totals.jobs_created}</p>
                          <p className="text-xs text-slate-400">Jobs Created</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-950/30 border-blue-800/50">
                        <CardContent className="pt-4 text-center">
                          <p className="text-2xl font-bold text-blue-400">{importResult.totals.customers_created}</p>
                          <p className="text-xs text-slate-400">Customers Created</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-950/30 border-purple-800/50">
                        <CardContent className="pt-4 text-center">
                          <p className="text-2xl font-bold text-purple-400">{importResult.totals.users_created}</p>
                          <p className="text-xs text-slate-400">Users Created</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-orange-950/30 border-orange-800/50">
                        <CardContent className="pt-4 text-center">
                          <p className="text-2xl font-bold text-orange-400">{importResult.totals.markets_created}</p>
                          <p className="text-xs text-slate-400">Markets Created</p>
                        </CardContent>
                      </Card>
                    </div>

                    {importResult.errors?.length > 0 && (
                      <Card className="bg-red-950/30 border-red-800/50">
                        <CardHeader>
                          <CardTitle className="text-sm text-red-400">Errors ({importResult.errors.length})</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {importResult.errors.slice(0, 10).map((err, idx) => (
                              <div key={idx} className="text-xs text-slate-400">
                                Row {err.row}: {err.error}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <div className="flex gap-4 justify-center">
                      <Button variant="outline" onClick={resetWizard}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        New Import
                      </Button>
                      <Button onClick={() => window.location.href = '/jobs'}>
                        <Briefcase className="h-4 w-4 mr-2" />
                        View Imported Jobs
                      </Button>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                {wizardStep < 5 && (
                  <div className="flex justify-between pt-6 border-t border-slate-800 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => setWizardStep(prev => Math.max(0, prev - 1))}
                      disabled={wizardStep === 0}
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      onClick={() => setWizardStep(prev => Math.min(4, prev + 1))}
                      disabled={!canProceed()}
                    >
                      {wizardStep === 4 ? 'Import' : 'Next'}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle>Import History</CardTitle>
                <CardDescription>Previous import batches with rollback options</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingBatches ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : batches.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No import batches yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {batches.map(batch => (
                      <div key={batch.id} className="p-4 bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Badge variant={batch.status === 'completed' ? 'default' : batch.status === 'rolled_back' ? 'secondary' : 'destructive'}>
                              {batch.status}
                            </Badge>
                            <span className="text-sm text-slate-400">
                              {new Date(batch.created_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {batch.status === 'completed' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRollback(batch.id)}
                                className="text-red-400 border-red-400/50 hover:bg-red-500/20"
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Rollback
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`${API}/data-tools/batches/${batch.id}/errors`, '_blank')}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Errors
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-slate-500">Jobs:</span> {batch.totals?.jobs_created || 0}
                          </div>
                          <div>
                            <span className="text-slate-500">Customers:</span> {batch.totals?.customers_created || 0}
                          </div>
                          <div>
                            <span className="text-slate-500">Users:</span> {batch.totals?.users_created || 0}
                          </div>
                          <div>
                            <span className="text-slate-500">Errors:</span> {batch.totals?.errors || 0}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default DataTools;
